/* eslint-disable indent */
/* eslint-disable operator-linebreak */
import {
  Avatar,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  ArchiveOutlined,
  CardMembershipOutlined,
  Description,
  FiberNew,
  PaymentOutlined
} from '@material-ui/icons';
import filesize from 'filesize';
import moment from 'moment';
import {PropTypes} from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {getCostInformationByPersonId} from '../script/cost-information-service';
import {
  getAllPersonDocuments,
  getPersonDocuments
} from '../script/document-service';
import {getPersonById} from '../script/person-service';
import {addNotifications} from '../store/appActions';

const useStyles = {
  table: {width: '100%', height: 240},
  documentTable: {width: '100%'},
  headline: {paddingTop: '3%', paddingBottom: '1%', color: 'white'},
  listPaper: {overflow: 'auto', maxHeight: 300, maxWidth: 360},
  listBody: {height: 212, overflow: 'auto'},
  tableIcon: {float: 'right'},
  chipDocumentName: {width: '12em', border: 0, justifyContent: 'left'}
};

const DAYS_UNTIL_CERTIFICATE_EXPIRING_NOTIFICATION = 30;
const LAST_DAY_UNTIL_CERTIFICATE_EXPIRING_NOTIFICATION = 0;
const ONE_DAY_UNTIL_CERTIFICATE_EXPIRING_NOTIFICATION = 1;
const OVERVIEW_TYPE_ARCHIVE = 'ARCHIVE';
const OVERVIEW_TYPE_CERTIFICATE = 'CERTIFICATE';
const OVERVIEW_TYPE_COSTS = 'COSTS';
const OVERVIEW_TYPE_DOCUMENTS = 'DOCUMENTS';
const EMPTY_VALUE = '-';

/**
 * Map the properties.x
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    addNotifications: (notification) => dispatch(addNotifications(notification))
  };
}

/**
 * Map the state to the properties.
 *
 * @param {Object} state - The state from store.
 * @return {Object} - The state.
 */
const mapStateToProps = (state: Object) => {
  return {
    user: state.user
  };
};

/**
 *
 * @param {string} value - The value that shall be displayed.
 * @return {string} - Returns the value to display.
 */
const getDisplayString = (value: string) => {
  return value ?? EMPTY_VALUE;
};

/**
 * Connected component class of Dashboard.
 */
class ConnectedDashboard extends Component {
  // eslint-disable-next-line require-jsdoc
  constructor(props) {
    super(props);
    this.currentDate = moment().format(locale.dateFormat);
    this.startDateMonth = moment().startOf('month').format(locale.dateFormat);
    this.startDateNewDocuments = moment()
      .subtract(7, 'd')
      .format(locale.dateFormat);
    this.state = {
      userId: props.user.id,
      archivedDocumentsMonth: null,
      archivedDocumentsTotal: null,
      certificate: {},
      certificateExpiringDate: '',
      subject: '',
      documentCounts: {},
      costInformation: {}
    };
  }

  /**
   * Fetches the archive information.
   */
  fetchArchiveInformation = () => {
    getAllPersonDocuments(false).then((response) => {
      this.setState({archivedDocumentsTotal: response.total});
    });

    getPersonDocuments(this.startDateMonth, this.currentDate, true).then(
      (response) => {
        this.setState({
          archivedDocumentsMonth: response.total
        });
      }
    );
  };

  /**
   * Displays a certificate expiring notification.
   */
  expiringCertificateNotification = () => {
    const expiringDate = this.state.certificateExpiringDate;
    const daysUntilExpiring = moment(expiringDate).diff(
      this.state.currentDate,
      'days'
    );
    let notificationText = '';

    if (daysUntilExpiring < DAYS_UNTIL_CERTIFICATE_EXPIRING_NOTIFICATION) {
      let certificateExpiringText = '';
      if (
        daysUntilExpiring ===
          LAST_DAY_UNTIL_CERTIFICATE_EXPIRING_NOTIFICATION ||
        daysUntilExpiring === ONE_DAY_UNTIL_CERTIFICATE_EXPIRING_NOTIFICATION
      ) {
        certificateExpiringText = locale.certificateExpiringNotification;
      } else {
        certificateExpiringText =
          daysUntilExpiring < LAST_DAY_UNTIL_CERTIFICATE_EXPIRING_NOTIFICATION
            ? locale.certificateExpiredNotification
            : locale.certificateExpiringDaysNotification;
      }

      notificationText = locale.formatString(
        certificateExpiringText,
        daysUntilExpiring
      );
      this.props.addNotifications([
        new Notification(notificationText, 'error', 6000)
      ]);
    } else {
      // TODO: Test notification, to be removed.
      // notificationText = 'Ihr Zertifikat ist noch ' + daysUntilExpiring + ' Tage gültig.';
      // this.props.addNotifications([new Notification(notificationText, 'success', 6000)]);
    }
  };

  /**
   * Fetches the certificate information.
   */
  fetchCertificateInformation = () => {
    if (this.state.userId) {
      getPersonById(this.state.userId).then((response) => {
        this.setState({
          certificate: response.certificate,
          certificateExpiringDate: response.certificate.usedUntil,
          subject: response.certificate.subject
        });
        this.expiringCertificateNotification();
      });
    }
  };

  /**
   * Fetches the documents information.
   */
  fetchDocumentsInformation = () => {
    getPersonDocuments().then((response) => {
      this.setState({
        documentCounts: response
      });
    });
  };

  /**
   * Fetches the cost information of the current month.
   */
  fetchCostInformation = () => {
    if (this.state.userId) {
      getCostInformationByPersonId().then((response) => {
        this.setState({costInformation: getDisplayString(response)});
      });
    }
  };

  componentDidMount = () => {
    // this.fetchArchiveInformation();
    this.fetchCertificateInformation();
    this.fetchDocumentsInformation();
    this.fetchCostInformation();
  };

  /**
   * Determines the icon for the specific overview.
   *
   * @param {string} overviewType - The type of the overview to display the table icon.
   * @return {string} - The icon component to be rendered.
   */
  getOverviewIcon = (overviewType: string) => {
    const iconStyle = useStyles.tableIcon;
    switch (overviewType) {
      case OVERVIEW_TYPE_ARCHIVE:
        return <ArchiveOutlined style={iconStyle} />;
      case OVERVIEW_TYPE_CERTIFICATE:
        return <CardMembershipOutlined style={iconStyle} />;
      case OVERVIEW_TYPE_DOCUMENTS:
        return <FiberNew style={iconStyle} />;
      case OVERVIEW_TYPE_COSTS:
        return <PaymentOutlined style={iconStyle} />;
      default:
        return false;
    }
  };

  /**
   * Renders a table head element with a given title.
   *
   * @param {string} tableTitle - The table title.
   * @param {string} overviewType - The type of the overview to display the table icon.
   * @return {string} - The toolbar component to be rendered.
   */
  renderTableHead = (tableTitle: string, overviewType: string) => {
    return (
      <TableHead>
        <TableRow>
          <TableCell>
            <b>{tableTitle}</b>
          </TableCell>
          <TableCell>{this.getOverviewIcon(overviewType)}</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  /**
   * Renders a table body element with given row data.
   *
   * @param {Object} rowData - The row data.
   * @param {boolean} displayCosts - The value indicating whether to display costs with currency.
   * @return {string} - The toolbar component to be rendered.
   */
  renderTableBody = (rowData: Object, displayCosts: boolean) => {
    const renderRowValue = (value: string) => {
      if (displayCosts) {
        return value === EMPTY_VALUE
          ? EMPTY_VALUE
          : locale.swissCurrencyPrefix + value + locale.swissCurrencySuffix;
      } else {
        return value;
      }
    };
    return (
      <TableBody>
        {rowData.map((row) => (
          <TableRow key={row.name}>
            <TableCell component='th' scope='row'>
              {row.name}
            </TableCell>
            <Tooltip title={row.tooltip ?? ''} placement={row.tooltipPlacement}>
              <TableCell align='right'>{renderRowValue(row.value)}</TableCell>
            </Tooltip>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  /**
   * Sub-render method for the archive information.
   *
   * @return {string} - The table container component.
   */
  renderArchiveInformation = () => {
    const archiveInformationRowData = [
      {
        name: locale.dashboardDocumentCount,
        value: this.state.documentCounts.total
      },
      {
        name: locale.dashboardDocumentThisMonth,
        value: this.state.documentCounts.month
      },
      {
        name: locale.dashboardArchiveSize,
        value: this.state.documentCounts.archiveSize
          ? filesize(this.state.documentCounts.archiveSize)
          : ''
      }
    ];
    return (
      <TableContainer component={Paper}>
        <Table style={useStyles.table} aria-label='archive-information'>
          {this.renderTableHead(
            locale.dashboardArchiveOverviewTitle,
            OVERVIEW_TYPE_ARCHIVE
          )}
          {this.renderTableBody(archiveInformationRowData, false)}
        </Table>
      </TableContainer>
    );
  };

  /**
   * Sub-render method for the certificate information.
   *
   * @return {string} - The table container component.
   */
  renderCertificateInformation = () => {
    const expirationDate = this.state.certificateExpiringDate;
    const certificateInformationRowData = [
      {
        name: locale.dashboardCertificateValidTo,
        value: expirationDate
          ? moment(expirationDate).format(locale.europeDateFormat)
          : EMPTY_VALUE
      },
      {
        name: locale.dashboardCertificateSubject,
        value: this.state.subject
          ? this.state.subject.replace(/,/g, ', ').substring(0, 100) + '..'
          : ''
        // FIXME: only add comma with whitespace ', ' if no whitespace or end of line follows the comma
        // FIXME: only add '..' if the string is actually longer than 100 characters
      }
    ];
    return (
      <TableContainer component={Paper}>
        <Table style={useStyles.table} aria-label='certificate-information'>
          {this.renderTableHead(
            locale.dashboardCertificateOverviewTitle,
            OVERVIEW_TYPE_CERTIFICATE
          )}
          {this.renderTableBody(certificateInformationRowData, false)}
        </Table>
      </TableContainer>
    );
  };

  /**
   * Sub-render method for the document list.
   *
   * @param {Array} documents - The list of documents.
   * @return {string} - The list component to be displayed.
   */
  renderDocumentsList = (documents: Array) => {
    const documentsList = documents.map((document, index) => (
      <ListItem key={index} divider={true}>
        <ListItemIcon>
          <Description />
        </ListItemIcon>
        <ListItemText>
          <Chip
            avatar={
              <Tooltip
                title={
                  document.contract ? locale.getString(document.contract) : ''
                }
                placement='top'
              >
                <Avatar>
                  {document.contract
                    ? locale.getString('tableColumn' + document.contract)
                    : ''}
                </Avatar>
              </Tooltip>
            }
            variant='outlined'
            size='small'
            style={useStyles.chipDocumentName}
            label={document.documentNumber}
          />
        </ListItemText>
        <ListItemSecondaryAction>
          <Typography variant='subtitle2'>
            {document.archivingDate
              ? moment(document.archivingDate).format(locale.europeDateFormat)
              : ''}
          </Typography>
        </ListItemSecondaryAction>
      </ListItem>
    ));

    return (
      <List style={useStyles.listBody} dense={true}>
        {documentsList}
      </List>
    );
  };

  /**
   * Sub-render method for the new document information.
   *
   * @return {string} - The table container component.
   */
  renderDocumentsInformation = () => {
    return (
      <TableContainer component={Paper}>
        <Table
          style={useStyles.documentTable}
          aria-label='document-information'
        >
          {this.renderTableHead(
            locale.dashboardDocumentOverviewTitle,
            OVERVIEW_TYPE_DOCUMENTS
          )}
        </Table>
        {this.renderDocumentsList(
          this.state.documentCounts.documents
            ? this.state.documentCounts.documents
            : []
        )}
      </TableContainer>
    );
  };

  /**
   * Sub-render method for the cost information.
   *
   * @return {string} - The table container component.
   */
  renderCostInformation = () => {
    const TOOLTIP_PLACEMENT = 'right';
    const costInfo = this.state.costInformation;
    const getTooltipVAT = (vat: string) => {
      return vat
        ? locale.inclusiveVAT +
            locale.swissCurrencyPrefix +
            vat +
            locale.swissCurrencySuffix
        : '';
    };

    const costOverviewRowData = [
      {
        name:
          locale.dashboardCostArchiving +
          getDisplayString(costInfo.documentCount),
        value: getDisplayString(costInfo.documentTotalCosts),
        tooltip: getTooltipVAT(costInfo.documentVAT),
        tooltipPlacement: TOOLTIP_PLACEMENT
      },
      {
        name: locale.dashboardCostSeal + getDisplayString(costInfo.sealCount),
        value: getDisplayString(costInfo.sealCosts),
        tooltip: getTooltipVAT(costInfo.sealVAT),
        tooltipPlacement: TOOLTIP_PLACEMENT
      },
      {
        name:
          locale.dashboardCostSignatures +
          getDisplayString(costInfo.signatureCount),
        value: getDisplayString(costInfo.signatureCosts),
        tooltip: getTooltipVAT(costInfo.signatureVAT),
        tooltipPlacement: TOOLTIP_PLACEMENT
      },
      {
        name: locale.dashboardCostOverall,
        value: getDisplayString(costInfo.totalCosts),
        tooltip: getTooltipVAT(costInfo.totalVAT),
        tooltipPlacement: TOOLTIP_PLACEMENT
      }
    ];
    return (
      <TableContainer component={Paper}>
        <Table style={useStyles.table} aria-label='cost-overview'>
          {this.renderTableHead(
            locale.dashboardCostOverviewTitle,
            OVERVIEW_TYPE_COSTS
          )}
          {this.renderTableBody(costOverviewRowData, true)}
        </Table>
      </TableContainer>
    );
  };

  /**
   * React render function.
   *
   * @return {string} - The component contents.
   */
  render() {
    return (
      <div>
        <Typography variant='h5' gutterBottom style={useStyles.headline}>
          {locale.dashboardHeadline}
        </Typography>
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={6}>
            {this.renderArchiveInformation()}
          </Grid>
          <Grid item xs={6}>
            {this.renderCertificateInformation()}
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={6}>
            {this.renderDocumentsInformation()}
          </Grid>
          <Grid item xs={6}>
            {this.renderCostInformation()}
          </Grid>
        </Grid>
      </div>
    );
  }
}

ConnectedDashboard.propTypes = {
  addNotifications: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired
};

const Dashboard = connect(
  mapStateToProps,
  mapDispatchToProps
)(ConnectedDashboard);
export default Dashboard;
